import { useEffect, useRef, useState } from "react"
import { Alert, Button, LinearProgress, Stack, Typography } from "@mui/material"
import { Modal } from "@core/components/modal"
import type { Media } from "../../interface/media"
import type { MediaWriteAccess } from "../../hooks/useMediaUploads"
import {
  useDeleteMediaMutation,
  useLazyGetMediaQuery,
} from "../../services/publications/mediaApi"
import { readPostBotError } from "../../utils/apiErrors"
import { FeatureErrors } from "../common/featureErrors"

export interface MediaDeleteDialogProps extends MediaWriteAccess {
  media: Media
  onDeleted: (id: string) => void
  onClose: () => void
}
// Keep optional publication context if supplied by a newer server, without
// widening shared error contracts or trusting arbitrary metadata as markup.
const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {}
function locations(error: unknown) {
  const outer = record(error)
  const envelope = record(record(outer.data ?? outer).error)
  const values = record(envelope.meta).locations
  if (!Array.isArray(values)) return []
  const labels: Record<string, string> = {
    templateId: "Шаблон",
    versionId: "Версия",
    occurrenceId: "Снимок",
    purpose: "Назначение",
    placementId: "Размещение",
    publicationId: "Публикация",
    position: "Позиция",
  }
  return values
    .map((value) =>
      Object.entries(labels)
        .flatMap(([key, label]) => {
          const fact = record(value)[key]
          return typeof fact === "string" ||
            (typeof fact === "number" && Number.isSafeInteger(fact))
            ? [`${label}: ${fact}`]
            : []
        })
        .join(" · "),
    )
    .filter(Boolean)
}

export function MediaDeleteDialog(props: MediaDeleteDialogProps) {
  return <DeleteAttempt key={props.media.id} {...props} />
}

function DeleteAttempt({
  media,
  onDeleted,
  onClose,
  readOnly,
  canWrite,
}: MediaDeleteDialogProps) {
  const [remove] = useDeleteMediaMutation()
  const [get] = useLazyGetMediaQuery()
  const [error, setError] = useState<unknown>()
  const [readError, setReadError] = useState<unknown>()
  const [status, setStatus] = useState<Media["status"]>()
  const [busy, setBusy] = useState(false)
  const [completed, setCompleted] = useState(false)
  const mounted = useRef(false)
  const locked = useRef(false)
  const done = useRef(false)
  const callback = useRef(onDeleted)
  callback.current = onDeleted
  const writable = !readOnly && canWrite !== false
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const complete = () => {
    if (!mounted.current || done.current) return
    done.current = true
    setCompleted(true)
    callback.current(media.id)
  }
  const reconcile = async () => {
    setReadError(undefined)
    try {
      const remote = await get(media.id, false).unwrap()
      if (mounted.current) setStatus(remote.status)
    } catch (error) {
      if (!mounted.current) return
      if (readPostBotError(error).status === 404) complete()
      else setReadError(error)
    }
  }
  const run = async (deleteFirst: boolean) => {
    if (
      !mounted.current ||
      locked.current ||
      done.current ||
      (deleteFirst && !writable)
    )
      return
    locked.current = true
    setBusy(true)
    try {
      if (!deleteFirst) await reconcile()
      else {
        setReadError(undefined)
        setStatus(undefined)
        try {
          await remove(media.id).unwrap()
          complete()
        } catch (error) {
          if (!mounted.current) return
          setError(error)
          const parsed = readPostBotError(error)
          // Definite client/reference failures do not need recovery. A lost or
          // unparseable success and dependency/network failures do.
          if (
            typeof parsed.status !== "number" ||
            parsed.status >= 500 ||
            parsed.status < 400
          )
            await reconcile()
        }
      }
    } finally {
      locked.current = false
      if (mounted.current) setBusy(false)
    }
  }
  const parsed = error == null ? undefined : readPostBotError(error)
  return (
    <Modal
      title="Удаление медиа"
      onClose={busy ? undefined : onClose}
      actions={[
        <Button key="close" disabled={busy} onClick={onClose}>
          Закрыть
        </Button>,
        <Button
          key="delete"
          color="error"
          disabled={!writable || busy || completed}
          onClick={() => void run(true)}
        >
          {error != null || media.status === "deleting"
            ? "Повторить DELETE того же ID"
            : "Удалить из библиотеки"}
        </Button>,
      ]}
    >
      <Stack spacing={2}>
        <Typography>{media.originalFilename}</Typography>
        <Typography sx={{ overflowWrap: "anywhere" }}>
          ID: {media.id}
        </Typography>
        <Alert severity="warning">
          Это удаление ресурса из библиотеки, а не отсоединение вложения.
          Проверяются все версии и история.
        </Alert>
        {busy && <LinearProgress />}
        {completed ? (
          <Alert severity="success">Удаление завершено.</Alert>
        ) : (
          <>
            <FeatureErrors error={error} />
            {parsed?.code === "MEDIA_IN_USE" && (
              <ul>
                {locations(error).map((value, index) => (
                  <li key={index}>{value}</li>
                ))}
              </ul>
            )}
            {status && (
              <Typography>
                Состояние: {status}.{" "}
                {status === "deleting"
                  ? "Можно явно повторить DELETE того же ID."
                  : "Ресурс не удалён. Проверьте исходную ошибку; не удаляйте историю для обхода ссылок."}
              </Typography>
            )}
            <FeatureErrors error={readError} />
            {error != null && (
              <Button disabled={busy} onClick={() => void run(false)}>
                Проверить состояние
              </Button>
            )}
          </>
        )}
      </Stack>
    </Modal>
  )
}
